/**
 * P02. TileMap Object Layer loader — parses Tiled-compatible JSON maps and
 * extracts spawn / obstacle / item lists for the horizontal battle scene.
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md P02. Boss-designer (future) edits the
 * .json map (in Tiled or by hand) and ships an updated wave layout / obstacle
 * positions without recompiling. The scene reads the object layers via
 * `loadMapObjects(scene, mapKey)` and:
 *   - spawns one HEnemy per `spawns` entry (variant from property)
 *   - feeds `obstacles` rects into the G06 Pathfinder.setObstacles(...)
 *   - drops `items` (pickups, freeXP, etc.) at the named coordinates
 *
 * Design rules:
 * - Phaser-free core: `parseMapObjects(json)` is a pure function that takes
 *   a parsed Tiled JSON object and returns { spawns, obstacles, items }. The
 *   scene-side helper `loadMapObjects(scene, mapKey)` is the thin Phaser
 *   wrapper that pulls the JSON out of the scene's cache. Tests exercise the
 *   pure function with hand-built fixtures + the real demo-map.json — they
 *   don't need a Phaser scene.
 * - Tiled JSON quirks we tolerate:
 *     - `properties` is an array of `{ name, type, value }` records (Tiled
 *       1.10+) — we normalise into a flat `{ name: value }` map.
 *     - Object layers may be missing entirely (e.g. items optional); we return
 *       empty arrays rather than throw so a stripped-down map still loads.
 *     - Point objects (spawns) have `width: 0, height: 0` — we don't validate
 *       that, we just use `x, y`.
 *     - Rectangle objects (obstacles) have `width, height > 0` — `x, y` is the
 *       top-left corner in world px (Tiled's default for orthogonal maps).
 * - Strict-ish: parse errors include the layer name / object id so a typo in
 *   the Tiled file is debuggable from the console without booting Phaser.
 *
 * Why a layer toggle in the scene (not a hard cutover): the existing
 * wave-spawn loop is shipped + tested. We add the TileMap path as an opt-in
 * (`useTileMap: true` in the scene init data — or a global feature flag) so
 * QA can A/B it against the random spawn layout before we flip the default.
 * This is the "default 維持既有" requirement called out in the dispatch.
 */

import type { ObstacleRect } from '../ai/pathfinder';

/** A single spawn point loaded from a Tiled "spawns" object layer. */
export interface SpawnPoint {
  /** World x in pixels (Tiled object x). */
  x: number;
  /** World y in pixels (Tiled object y). */
  y: number;
  /** Tiled object `name` field. May be empty if author forgot. */
  name: string;
  /**
   * Spawn variant — pulled from a `variant` custom property if present (e.g.
   * "basic" / "fast" / "tank"), otherwise falls back to `name`. Optional so
   * authors can write either pattern.
   */
  variant?: string;
  /** Free-form Tiled properties as a flat map. */
  properties: Record<string, string | number | boolean>;
}

/** An item / pickup point loaded from a Tiled "items" object layer. */
export interface ItemPoint {
  /** World x in pixels. */
  x: number;
  /** World y in pixels. */
  y: number;
  /** Tiled object `name` — typically the item kind ("freeXP", "heal", etc.). */
  name: string;
  /** Free-form Tiled properties (e.g. { amount: 10 }). */
  properties: Record<string, string | number | boolean>;
}

/** Result of parsing a Tiled JSON map for the battle scene. */
export interface MapObjects {
  /** Enemy spawn points; one HEnemy per entry. Order = file order. */
  spawns: SpawnPoint[];
  /** Axis-aligned rects for G06 Pathfinder.setObstacles(...). */
  obstacles: ObstacleRect[];
  /** Pickup / item drop points. */
  items: ItemPoint[];
  /** Map dimensions in world px (width = tilewidth × map.width, etc.). */
  worldWidth: number;
  /** Map dimensions in world px. */
  worldHeight: number;
}

/** Minimal Tiled JSON shape we depend on. Extra fields are ignored. */
export interface TiledMapJson {
  width: number;
  height: number;
  tilewidth: number;
  tileheight: number;
  layers: TiledLayer[];
}

interface TiledLayer {
  name: string;
  type: string;
  objects?: TiledObject[];
}

interface TiledObject {
  id?: number;
  name?: string;
  type?: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  properties?: Array<{ name: string; type?: string; value: string | number | boolean }>;
}

/** Layer name constants — keep authors honest about expected names. */
export const LAYER_SPAWNS = 'spawns';
export const LAYER_OBSTACLES = 'obstacles';
export const LAYER_ITEMS = 'items';

/**
 * Normalise Tiled's `properties: [{name, value}, ...]` array into a flat
 * `Record<name, value>` map. Missing / undefined → empty object.
 */
function normaliseProperties(
  props: TiledObject['properties'],
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!props) return out;
  for (const p of props) {
    if (p && typeof p.name === 'string') out[p.name] = p.value;
  }
  return out;
}

/**
 * Find a Tiled object layer by name. Returns undefined when missing (callers
 * decide whether that's fatal or just an empty list).
 */
function findObjectLayer(json: TiledMapJson, layerName: string): TiledLayer | undefined {
  return json.layers.find((l) => l.type === 'objectgroup' && l.name === layerName);
}

/**
 * Parse a Tiled JSON map into the spawn / obstacle / item lists the battle
 * scene consumes. Pure function — no Phaser, no IO. Throws on missing
 * required top-level fields (so a bad map fails LOUD at startup, not when
 * the first wave tries to spawn).
 *
 * Missing optional layers (`items`, or even `spawns` if a level is boss-only)
 * → empty arrays. Missing `obstacles` → empty (the level just has no
 * pathfinder obstacles, fine).
 *
 * @param json Parsed Tiled map JSON.
 * @returns Extracted spawn / obstacle / item arrays + world dimensions.
 */
export function parseMapObjects(json: TiledMapJson): MapObjects {
  if (!json || typeof json !== 'object') {
    throw new Error('parseMapObjects: json must be an object');
  }
  if (typeof json.width !== 'number' || typeof json.height !== 'number') {
    throw new Error('parseMapObjects: json.width / json.height must be numbers');
  }
  if (typeof json.tilewidth !== 'number' || typeof json.tileheight !== 'number') {
    throw new Error('parseMapObjects: json.tilewidth / json.tileheight must be numbers');
  }
  if (!Array.isArray(json.layers)) {
    throw new Error('parseMapObjects: json.layers must be an array');
  }

  const worldWidth = json.width * json.tilewidth;
  const worldHeight = json.height * json.tileheight;

  const spawnLayer = findObjectLayer(json, LAYER_SPAWNS);
  const obstacleLayer = findObjectLayer(json, LAYER_OBSTACLES);
  const itemLayer = findObjectLayer(json, LAYER_ITEMS);

  const spawns: SpawnPoint[] = [];
  if (spawnLayer?.objects) {
    for (const obj of spawnLayer.objects) {
      const properties = normaliseProperties(obj.properties);
      const variantRaw = properties['variant'];
      spawns.push({
        x: obj.x,
        y: obj.y,
        name: obj.name ?? '',
        variant: typeof variantRaw === 'string' ? variantRaw : obj.name || undefined,
        properties,
      });
    }
  }

  const obstacles: ObstacleRect[] = [];
  if (obstacleLayer?.objects) {
    for (const obj of obstacleLayer.objects) {
      const w = typeof obj.width === 'number' ? obj.width : 0;
      const h = typeof obj.height === 'number' ? obj.height : 0;
      if (w <= 0 || h <= 0) {
        // Skip degenerate point-objects on the obstacles layer — they'd block
        // nothing anyway and would foul up the A* grid build. Authors who want
        // a single-cell obstacle should give it width/height = tilesize.
        continue;
      }
      obstacles.push({ x: obj.x, y: obj.y, width: w, height: h });
    }
  }

  const items: ItemPoint[] = [];
  if (itemLayer?.objects) {
    for (const obj of itemLayer.objects) {
      items.push({
        x: obj.x,
        y: obj.y,
        name: obj.name ?? '',
        properties: normaliseProperties(obj.properties),
      });
    }
  }

  return { spawns, obstacles, items, worldWidth, worldHeight };
}

/**
 * Minimal Phaser scene shape we need to pull the Tiled JSON out of the scene's
 * cache. Defined locally so this module stays Phaser-free at the import level
 * — `loadMapObjects` accepts any object with a compatible `cache.tilemap` or
 * `cache.json` shape. In production the caller passes the real Phaser scene.
 */
export interface TilemapCacheLike {
  cache: {
    tilemap?: { get(key: string): { data: TiledMapJson } | TiledMapJson | undefined };
    json?: { get(key: string): TiledMapJson | undefined };
  };
}

/**
 * Phaser-wrapper around `parseMapObjects`. Reads the parsed JSON from the
 * scene's `cache.tilemap` (Phaser populates this for `this.load.tilemapTiledJSON`)
 * or `cache.json` (when the author loaded the map as a plain JSON file via
 * `this.load.json`). Returns null when the key is absent so callers can fall
 * back to the hardcoded spawn path without crashing the scene.
 *
 * Boss-designer flow:
 *   1. In BootScene / preload: `this.load.tilemapTiledJSON('demo-map', 'assets/maps/demo-map.json')`
 *   2. In BattleScene.create(): `const objs = loadMapObjects(this, 'demo-map');`
 *   3. If objs is non-null: iterate `objs.spawns` + apply `objs.obstacles` to
 *      the Pathfinder.
 *
 * @param scene Phaser scene (or anything that quacks like one — see TilemapCacheLike).
 * @param mapKey Cache key passed to load.tilemapTiledJSON / load.json.
 * @returns Parsed MapObjects, or null when the key is missing from cache.
 */
export function loadMapObjects(scene: TilemapCacheLike, mapKey: string): MapObjects | null {
  if (!scene?.cache) return null;
  // Prefer the tilemap cache — that's what Phaser populates from
  // load.tilemapTiledJSON and the entry shape is { data: <json> }.
  const tmEntry = scene.cache.tilemap?.get(mapKey);
  let json: TiledMapJson | undefined;
  if (tmEntry) {
    // Phaser's tilemap cache stores `{ data, format, ... }`. We want `.data`
    // when present, otherwise treat the entry itself as the JSON (test stubs
    // sometimes set the raw json straight in).
    const maybeWrapped = tmEntry as { data?: TiledMapJson } & TiledMapJson;
    json = maybeWrapped.data ?? maybeWrapped;
  } else {
    json = scene.cache.json?.get(mapKey);
  }
  if (!json) return null;
  return parseMapObjects(json);
}
